import React, { useState, useEffect } from "react";
import { useForm } from "react-hook-form";
import { z } from "zod";
import {
  Box,
  Typography,
  Button,
  Avatar,
  Divider,
  CircularProgress,
  alpha,
  useTheme,
} from "@mui/material";
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import CameraAltOutlinedIcon from "@mui/icons-material/CameraAltOutlined";
import VerifiedIcon from "@mui/icons-material/Verified";
import { MuiTelInput, matchIsValidTel } from "mui-tel-input";
import {
  useGetProfileQuery,
  useUpdateProfilePicMutation,
  useUpdatePhoneMutation,
  useGetStatusQuery,
} from "components/Slices/usersApiSlice";
import { getUrl, uploadData } from "aws-amplify/storage";
import { getCurrentUser } from "aws-amplify/auth";
import { useImageUrl } from "hooks/useImageUrl";

const profileFormSchema = z.object({
  displayImg: z.string().optional(),
  firstName: z.string().min(2),
  lastName: z.string().min(2),
  email: z.string().min(1).email(),
  bio: z.string().max(160).min(4).optional(),
  country: z.string().optional(),
});

type ProfileFormValues = z.infer<typeof profileFormSchema>;

const InfoRow = ({ label, value }: { label: string; value?: string }) => (
  <Box>
    <Typography
      sx={{
        fontSize: "0.78rem",
        fontWeight: 700,
        color: "text.disabled",
        textTransform: "uppercase",
        letterSpacing: "0.1em",
        mb: 0.4,
      }}
    >
      {label}
    </Typography>
    <Typography
      sx={{ fontSize: "0.9rem", fontWeight: 500, color: "text.primary" }}
    >
      {value || "—"}
    </Typography>
  </Box>
);

export function ProfileForm() {
  const theme = useTheme();
  const { data: userProfile, isLoading } = useGetProfileQuery();
  const { data: statusData } = useGetStatusQuery();
  const isVerified = statusData?.userStatus?.toLowerCase() === "active";
  const { data: profilePictureUrl, isLoading: isLoadingImageUrl } = useImageUrl(
    userProfile?.displayImg,
  );
  const [helperMessage, setHelperMessage] = useState<string | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [updateProfilePic] = useUpdateProfilePicMutation();
  const [updatePhone, { isLoading: isSavingPhone }] = useUpdatePhoneMutation();
  const [phoneDraft, setPhoneDraft] = useState("");
  const [phoneDirty, setPhoneDirty] = useState(false);
  const [phoneMsg, setPhoneMsg] = useState<{
    ok: boolean;
    text: string;
  } | null>(null);
  const { reset } = useForm<ProfileFormValues>();

  useEffect(() => {
    reset(userProfile);
    if (userProfile && !phoneDirty) {
      setPhoneDraft(userProfile.phone || "");
    }
  }, [userProfile, reset]);

  const handleSavePhone = async () => {
    const clearing = phoneDraft.trim() === "";
    if (!clearing && !matchIsValidTel(phoneDraft)) {
      setPhoneMsg({
        ok: false,
        text: "That doesn't look like a valid number.",
      });
      return;
    }
    try {
      await updatePhone({ phone: clearing ? null : phoneDraft }).unwrap();
      setPhoneDirty(false);
      setPhoneMsg({
        ok: true,
        text: clearing
          ? "Phone removed — SMS alerts off."
          : "Saved. Money alerts will be texted here.",
      });
    } catch (err: any) {
      setPhoneMsg({
        ok: false,
        text: err?.data?.error || "Couldn't save. Try again.",
      });
    }
  };

  if (isLoading || !userProfile) {
    return (
      <Box
        sx={{
          borderRadius: 4,
          bgcolor: "background.paper",
          boxShadow:
            "0 1px 3px rgba(16,24,64,0.06), 0 4px 16px rgba(16,24,64,0.08)",
          p: 4,
          display: "flex",
          justifyContent: "center",
        }}
      >
        <CircularProgress size={28} />
      </Box>
    );
  }

  const handleFileChange = async (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const validTypes = ["image/jpeg", "image/png", "image/gif"];
    if (!validTypes.includes(file.type)) {
      setHelperMessage("Please select a valid image file (JPG, PNG, GIF)");
      return;
    }
    if (file.size > 800 * 1024) {
      setHelperMessage("File size exceeds 800KB");
      return;
    }

    setHelperMessage(null);
    setIsUploading(true);
    try {
      const user = await getCurrentUser();
      const ext = file.name.split(".").pop() || "jpg";
      const filename = `${user.userId}_${Date.now()}.${ext}`;
      const result = await uploadData({
        path: `public/profilePics/${filename}`,
        data: file,
      }).result;
      await updateProfilePic({ profilePicture: result.path });
    } catch (err) {
      console.error("Upload failed:", err);
      setHelperMessage("Failed to upload. Please try again.");
    } finally {
      setIsUploading(false);
    }
  };

  // No external placeholder service: initials fallback keeps it on-brand and
  // avoids leaking user names to a third party.
  const avatarSrc = profilePictureUrl || undefined;
  const avatarInitials = `${userProfile.firstName?.[0] ?? ""}${
    userProfile.lastName?.[0] ?? ""
  }`.toUpperCase();

  return (
    <Box
      sx={{
        borderRadius: 4,
        bgcolor: "background.paper",
        boxShadow:
          "0 1px 3px rgba(16,24,64,0.06), 0 4px 16px rgba(16,24,64,0.08)",
        overflow: "hidden",
      }}
    >
      {/* Header */}
      <Box
        sx={{
          px: 3.5,
          pt: 3,
          pb: 2.5,
          borderBottom: "1px solid",
          borderColor: "divider",
          display: "flex",
          alignItems: "center",
          gap: 1.5,
        }}
      >
        <Box
          sx={{
            width: 32,
            height: 32,
            borderRadius: 1.5,
            bgcolor: alpha(theme.palette.primary.main, 0.08),
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flexShrink: 0,
          }}
        >
          <PersonOutlineIcon sx={{ fontSize: "1rem", color: "primary.main" }} />
        </Box>
        <Box>
          <Typography
            sx={{ fontSize: "0.95rem", fontWeight: 700, color: "text.primary" }}
          >
            Profile
          </Typography>
          <Typography sx={{ fontSize: "0.75rem", color: "text.secondary" }}>
            This is how others will see you on the site.
          </Typography>
        </Box>
      </Box>

      {/* Avatar section */}
      <Box
        sx={{
          px: 3.5,
          py: 3,
          display: "flex",
          alignItems: "center",
          gap: 3,
          borderBottom: "1px solid",
          borderColor: "divider",
        }}
      >
        <Box sx={{ position: "relative", flexShrink: 0 }}>
          <Avatar
            src={avatarSrc}
            alt={userProfile.firstName}
            sx={{
              width: 72,
              height: 72,
              bgcolor: "primary.main",
              fontWeight: 600,
              opacity: isUploading || isLoadingImageUrl ? 0.6 : 1,
            }}
          >
            {avatarInitials}
          </Avatar>
          <Box
            component="label"
            htmlFor="profile-pic"
            sx={{
              position: "absolute",
              bottom: 0,
              right: 0,
              width: 24,
              height: 24,
              borderRadius: "50%",
              bgcolor: "primary.main",
              border: "2px solid",
              borderColor: "background.paper",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              cursor: "pointer",
              "&:hover": { bgcolor: "primary.dark" },
            }}
          >
            {isUploading ? (
              <CircularProgress size={12} sx={{ color: "#fff" }} />
            ) : (
              <CameraAltOutlinedIcon
                sx={{ fontSize: "0.7rem", color: "#fff" }}
              />
            )}
          </Box>
          <input
            type="file"
            id="profile-pic"
            style={{ display: "none" }}
            onChange={handleFileChange}
            accept="image/*"
          />
        </Box>

        <Box>
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
            <Typography
              sx={{
                fontSize: "0.95rem",
                fontWeight: 600,
                color: "text.primary",
              }}
            >
              {userProfile.firstName} {userProfile.lastName}
            </Typography>
            {isVerified && (
              <VerifiedIcon sx={{ fontSize: "1rem", color: "primary.main" }} />
            )}
          </Box>
          <Typography
            sx={{ fontSize: "0.78rem", color: "text.secondary", mt: 0.25 }}
          >
            {userProfile.email}
          </Typography>
          {helperMessage && (
            <Typography
              sx={{ fontSize: "0.72rem", color: "error.main", mt: 0.5 }}
            >
              {helperMessage}
            </Typography>
          )}
          <Button
            component="label"
            htmlFor="profile-pic"
            variant="outlined"
            size="small"
            disabled={isUploading}
            sx={{
              mt: 1.25,
              fontSize: "0.75rem",
              fontWeight: 600,
              borderColor: "divider",
              color: "text.secondary",
              borderRadius: "8px",
              "&:hover": {
                borderColor: "primary.main",
                color: "primary.main",
                bgcolor: "transparent",
              },
            }}
          >
            {isUploading ? "Uploading…" : "Change photo"}
          </Button>
        </Box>
      </Box>

      {/* Info rows */}
      <Box
        sx={{
          px: 3.5,
          py: 3,
          display: "flex",
          flexDirection: "column",
          gap: 2.5,
        }}
      >
        <InfoRow
          label="Full Name"
          value={`${userProfile.firstName} ${userProfile.lastName}`}
        />
        <Divider />
        <InfoRow label="Email Address" value={userProfile.email} />
        <Divider />
        <InfoRow label="Country" value={userProfile.country} />
        <Divider />

        {/* Phone — drives SMS money alerts (helpers/sms.js on the backend). */}
        <Box>
          <Typography
            sx={{
              fontSize: "0.78rem",
              fontWeight: 700,
              color: "text.disabled",
              textTransform: "uppercase",
              letterSpacing: "0.1em",
              mb: 0.75,
            }}
          >
            Phone · SMS alerts
          </Typography>
          <MuiTelInput
            value={phoneDraft}
            onChange={(value) => {
              setPhoneDraft(value);
              setPhoneDirty(true);
              setPhoneMsg(null);
            }}
            // Model enum says "UK" but MuiTelInput wants ISO "GB".
            defaultCountry={
              userProfile.country === "UK" ? "GB" : userProfile.country || "CA"
            }
            size="small"
            fullWidth
            placeholder="Add a number for payment and payout texts"
            sx={{
              "& .MuiOutlinedInput-root": {
                borderRadius: 2,
                fontSize: "0.88rem",
              },
            }}
          />
          <Typography
            sx={{ fontSize: "0.85rem", color: "text.secondary", mt: 0.75 }}
          >
            Only money moments — payment due, payout on the way, verification
            deadlines. No marketing.
          </Typography>
          {phoneMsg && (
            <Typography
              sx={{
                fontSize: "0.85rem",
                mt: 0.5,
                color: phoneMsg.ok ? "success.main" : "error.main",
                fontWeight: 600,
              }}
            >
              {phoneMsg.text}
            </Typography>
          )}
          {phoneDirty && (
            <Button
              variant="contained"
              size="small"
              disabled={isSavingPhone}
              onClick={handleSavePhone}
              sx={{
                mt: 1.25,
                fontSize: "0.75rem",
                fontWeight: 600,
                borderRadius: "8px",
                textTransform: "none",
              }}
            >
              {isSavingPhone
                ? "Saving…"
                : phoneDraft.trim() === ""
                  ? "Remove number"
                  : "Save number"}
            </Button>
          )}
        </Box>
      </Box>
    </Box>
  );
}
